import type {
  HTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

/**
 * 卡片（shadcn/ui 风格最小实现）。
 *
 * 为什么值得单独抽一个组件：整个站点的"信息块"（词条卡、解析卡、统计块、复习卡）
 * 外观必须一致，而它们分散在 8 个页面里。靠每个页面各写一遍 `rounded-lg border bg-card`
 * 的结果就是圆角/阴影/内边距慢慢漂移。
 */
export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={cn('rounded-lg border bg-card text-card-foreground shadow-sm', className)} />;
}

export function CardHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={cn('flex items-start justify-between gap-3 border-b px-4 py-3', className)} />;
}

export function CardTitle({ className, children, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2 {...rest} className={cn('text-sm font-semibold', className)}>
      {children}
    </h2>
  );
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={cn('px-4 py-3', className)} />;
}

export function CardFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={cn('flex flex-wrap items-center gap-2 border-t px-4 py-3', className)} />;
}

/** 小块信息（词性、等级、标签）。`tone` 决定配色语义。 */
export function Badge({
  children,
  tone = 'default',
  className,
  title,
}: {
  children: ReactNode;
  tone?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'muted';
  className?: string;
  title?: string;
}) {
  const tones: Record<string, string> = {
    default: 'border-border bg-background text-foreground',
    primary: 'border-primary/30 bg-accent text-accent-foreground',
    success: 'border-transparent bg-[color-mix(in_srgb,var(--success)_18%,transparent)] text-[var(--success)]',
    warning: 'border-transparent bg-[color-mix(in_srgb,var(--warning)_18%,transparent)] text-[var(--warning)]',
    danger: 'border-transparent bg-[color-mix(in_srgb,var(--destructive)_16%,transparent)] text-destructive',
    muted: 'border-transparent bg-muted text-muted-foreground',
  };
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-5',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * 原生 select 的统一外观。刻意不用自定义下拉：移动端原生选择器体验更好。
 *
 * ⚠️ 类型必须是 `SelectHTMLAttributes`，不能写成 `HTMLAttributes`：
 * 后者不含 `value` / `onChange` / `multiple`，受控用法会直接编译不过 ——
 * 之前这里写错，导致 5 个页面各自加了一层局部类型断言来绕过，属于把问题扩散到了调用方。
 */
export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...rest}
      className={cn('h-9 rounded-md border bg-background px-2 text-sm disabled:opacity-60', className)}
    >
      {children}
    </select>
  );
}

/** 多行输入。同理，类型用 `TextareaHTMLAttributes` 才能接受 `rows` / `value` / `onChange`。 */
export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...rest}
      className={cn(
        'w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground',
        'disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-destructive',
        className,
      )}
    />
  );
}
